import React from 'react';
import './AboutPage.css';

interface AboutPageProps {
  onBackToHome: () => void;
  onGetStarted: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ onBackToHome, onGetStarted }) => {
  return (
    <div className="about-page">
      {/* Header */}
      <header className="about-page__header">
        <div className="about-page__container">
          <button className="about-page__back-button" onClick={onBackToHome}>
            ← Back to Home
          </button>
          <div className="about-page__header-content">
            <h1 className="about-page__title">About Faathum</h1>
            <p className="about-page__subtitle">
              Helping people move from data to understanding
            </p>
          </div>
        </div>
      </header>

      {/* Mission Section */}
      <section className="about-page__mission">
        <div className="about-page__container">
          <div className="about-page__mission-content">
            <h2>Our Mission</h2>
            <p className="about-page__mission-text">
              At Faathum, we believe that data should be accessible to everyone, not just data scientists. 
              Our mission is to democratize data exploration by creating intelligent interfaces that connect 
              users directly to the world's most advanced AI platforms with contextual, audience-specific prompts.
            </p>
            <p className="about-page__mission-text">
              We're building the bridge between complex datasets and actionable insights, making it possible 
              for journalists, researchers, policymakers, and curious minds to unlock the stories hidden in data.
            </p>
          </div>
          <div className="about-page__mission-visual">
            <div className="about-page__stats">
              <div className="about-page__stat">
                <div className="about-page__stat-number">5+</div>
                <div className="about-page__stat-label">AI Platforms</div>
              </div>
              <div className="about-page__stat">
                <div className="about-page__stat-number">4</div>
                <div className="about-page__stat-label">Audience Types</div>
              </div>
              <div className="about-page__stat">
                <div className="about-page__stat-number">∞</div>
                <div className="about-page__stat-label">Possibilities</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-page__story">
        <div className="about-page__container">
          <h2>Our Story</h2>
          <div className="about-page__story-content">
            <div className="about-page__story-text">
              <h3>The Problem We Saw</h3>
              <p>
                Organizations worldwide publish incredible datasets that could drive meaningful insights 
                and informed decision-making. However, these datasets often remain underutilized because 
                they lack accessible interfaces for exploration and analysis.
              </p>
              <p>
                Traditional data visualization tools require technical expertise, while AI platforms 
                need carefully crafted prompts to provide relevant insights. This creates a barrier 
                between valuable data and the people who could benefit from understanding it.
              </p>
            </div>
            <div className="about-page__story-text">
              <h3>Our Solution</h3>
              <p>
                Faathum bridges this gap by providing a no-code platform for creating intelligent 
                data exploration widgets. These widgets automatically generate contextual prompts 
                tailored to different audiences and connect users seamlessly to leading AI platforms.
              </p>
              <p>
                Whether you're a journalist investigating economic trends, a researcher analyzing 
                climate data, or a policymaker reviewing demographic statistics, Faathum ensures 
                you get the right insights in the right format.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="about-page__values">
        <div className="about-page__container">
          <h2>Our Values</h2>
          <div className="about-page__values-grid">
            <div className="about-page__value">
              <div className="about-page__value-icon">🌍</div>
              <h3>Accessibility</h3>
              <p>Data insights should be available to everyone, regardless of technical background or expertise level.</p>
            </div>
            <div className="about-page__value">
              <div className="about-page__value-icon">🔬</div>
              <h3>Accuracy</h3>
              <p>We prioritize reliable, well-sourced information and transparent methodologies in all our tools.</p>
            </div>
            <div className="about-page__value">
              <div className="about-page__value-icon">🤝</div>
              <h3>Collaboration</h3>
              <p>The best insights emerge when diverse perspectives can easily engage with and interpret data.</p>
            </div>
            <div className="about-page__value">
              <div className="about-page__value-icon">🚀</div>
              <h3>Innovation</h3>
              <p>We continuously evolve our platform to leverage the latest advances in AI and data science.</p>
            </div>
            <div className="about-page__value">
              <div className="about-page__value-icon">🛡️</div>
              <h3>Responsibility</h3>
              <p>We build with ethical AI practices and provide clear disclaimers about limitations and biases.</p>
            </div>
            <div className="about-page__value">
              <div className="about-page__value-icon">⚡</div>
              <h3>Efficiency</h3>
              <p>Our tools are designed to reduce the time between questions and actionable insights.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="about-page__team">
        <div className="about-page__container">
          <h2>Built for Impact</h2>
          <div className="about-page__team-content">
            <p>
              Faathum is built by a team passionate about the intersection of data, AI, and human understanding. 
              We come from backgrounds in data science, journalism, policy research, and technology, giving us 
              unique insight into the challenges faced by different types of data consumers.
            </p>
            <p>
              Our diverse perspectives help us build tools that truly serve the needs of journalists seeking 
              story angles, researchers conducting analysis, policymakers making decisions, and curious 
              individuals exploring the world through data.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="about-page__use-cases">
        <div className="about-page__container">
          <h2>Who We Serve</h2>
          <div className="about-page__use-cases-grid">
            <div className="about-page__use-case">
              <div className="about-page__use-case-icon">🏛️</div>
              <h3>Government Agencies</h3>
              <p>
                Enable citizens, researchers, and policymakers to explore public datasets with 
                AI-powered analysis tools that provide relevant insights for democratic engagement.
              </p>
              <div className="about-page__use-case-examples">
                <span>Economic Data</span>
                <span>Census Information</span>
                <span>Policy Impact</span>
              </div>
            </div>
            <div className="about-page__use-case">
              <div className="about-page__use-case-icon">📰</div>
              <h3>News Organizations</h3>
              <p>
                Provide journalists and readers with intelligent data exploration capabilities 
                for investigative reporting and informed public discourse.
              </p>
              <div className="about-page__use-case-examples">
                <span>Breaking News</span>
                <span>Trend Analysis</span>
                <span>Fact Checking</span>
              </div>
            </div>
            <div className="about-page__use-case">
              <div className="about-page__use-case-icon">🎓</div>
              <h3>Research Institutions</h3>
              <p>
                Offer students, faculty, and researchers AI-assisted analysis of academic 
                datasets and publications for enhanced research outcomes.
              </p>
              <div className="about-page__use-case-examples">
                <span>Academic Research</span>
                <span>Student Projects</span>
                <span>Publication Analysis</span>
              </div>
            </div>
            <div className="about-page__use-case">
              <div className="about-page__use-case-icon">🏢</div>
              <h3>Enterprises</h3>
              <p>
                Democratize data analysis across teams with user-friendly AI exploration 
                interfaces that don't require technical expertise.
              </p>
              <div className="about-page__use-case-examples">
                <span>Business Intelligence</span>
                <span>Market Research</span>
                <span>Performance Metrics</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="about-page__technology">
        <div className="about-page__container">
          <h2>Our Technology</h2>
          <div className="about-page__tech-content">
            <div className="about-page__tech-text">
              <h3>AI-Powered Prompt Generation</h3>
              <p>
                Our intelligent prompt generation system creates contextual queries tailored to 
                specific audiences and analysis goals. Whether you're looking for trends, anomalies, 
                or policy implications, our system crafts the right questions for AI platforms.
              </p>
              
              <h3>Multi-Platform Integration</h3>
              <p>
                We integrate with leading AI platforms including ChatGPT, Claude, Gemini, Perplexity, 
                and Grok, giving users flexibility to choose the AI that best fits their needs and preferences.
              </p>
              
              <h3>Responsive Widget Architecture</h3>
              <p>
                Our widgets are built with modern web technologies to ensure fast loading, responsive 
                design, and seamless integration with any website or application.
              </p>
            </div>
            <div className="about-page__tech-features">
              <div className="about-page__tech-feature">
                <h4>🎯 Smart Targeting</h4>
                <p>Audience-specific prompt customization</p>
              </div>
              <div className="about-page__tech-feature">
                <h4>🔗 Seamless Integration</h4>
                <p>One-click deployment to any website</p>
              </div>
              <div className="about-page__tech-feature">
                <h4>📊 Analytics Ready</h4>
                <p>Built-in tracking and usage analytics</p>
              </div>
              <div className="about-page__tech-feature">
                <h4>🛡️ Privacy Focused</h4>
                <p>Transparent data handling and user privacy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-page__cta">
        <div className="about-page__container">
          <div className="about-page__cta-content">
            <h2>Ready to Transform Your Data Experience?</h2>
            <p>
              Join organizations worldwide using Faathum to make data more accessible, 
              actionable, and engaging for their audiences.
            </p>
            <button className="about-page__cta-button" onClick={onGetStarted}>
              Start Building Your Widget
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
